<template>
  <v-simple-table>
    <thead v-if="!empty">
      <tr>
        <th class="text-left">
          {{ $t('fresh-vue-history.layout.table.headers.action') }}
        </th>
        <th class="text-center">
          {{ $t('fresh-vue-history.layout.table.headers.created_at') }}
        </th>
        <th class="text-left">
          {{ $t('fresh-vue-history.layout.table.headers.created_by') }}
        </th>
        <th class="text-left">
          {{ $t('fresh-vue-history.layout.table.headers.description') }}
        </th>
        <th v-if="showDuration" class="text-left">
          {{ $t('fresh-vue-history.layout.table.headers.duration') }}
          <history-view-duration :history="history">
            <template #default="{ duration }">
              <v-tooltip bottom>
                <template #activator="{ on }">
                  <v-icon x-small class="mr-2" v-on="on">
                    $freshvuehistory-duration
                  </v-icon>
                </template>
                {{
                  $t('fresh-vue-history.layout.table.headers.duration-tooltip')
                }}
              </v-tooltip>
              <small>{{ duration | humanizeDuration }}</small>
            </template>
          </history-view-duration>
        </th>
        <th class="text-left">
          {{ $t('fresh-vue-history.layout.table.headers.details') }}
        </th>
      </tr>
    </thead>

    <v-slide-x-transition v-if="!empty" group tag="tbody">
      <!-- Historie -->
      <tr v-for="item in history" :key="item.id">
        <!-- event -->
        <td class="text-no-wrap">
          <v-icon small class="mr-2">
            $freshvuehistory-event-{{ item.event }}
          </v-icon>
          <span class="mr-2">
            {{ getFormattedEventText(item) }}
          </span>
        </td>

        <!-- Datum -->
        <td class="text-no-wrap text-center">
          {{ item.created_at | date }}<br />
          {{ item.created_at | time }}
        </td>

        <!-- created_by -->
        <td>
          {{ item.created_by.name }}
        </td>

        <!-- description -->
        <td class="text-no-wrap">
          <long-text :text="item.description" />
        </td>

        <!-- duration -->
        <td v-if="showDuration" class="text-no-wrap">
          {{ item.duration | humanizeDuration }}
        </td>

        <!-- details -->
        <td class="text-no-wrap">
          <!-- details slot -->
          <slot name="details-prepend" :item="item" view="TABLE">
            <json-details v-if="item.data" dense :json="item.data" />
          </slot>

          <!-- audit -->
          <v-dialog
            v-if="item.audit && item.audit.event === 'updated'"
            max-width="800"
          >
            <template #activator="{ on: dialog }">
              <v-tooltip left>
                <template #activator="{ on: tooltip }">
                  <v-btn
                    small
                    icon
                    class="ma-0"
                    v-on="{ ...dialog, ...tooltip }"
                  >
                    <v-icon small>$freshvuehistory-show-history</v-icon>
                  </v-btn>
                </template>
                <span>{{
                  $t('fresh-vue-history.layout.table.actions.show-change')
                }}</span>
              </v-tooltip>
            </template>

            <card-base>
              <slot name="audit-changes" :audit="item.audit">
                <audit-changes :audit="item.audit" />
              </slot>
            </card-base>
          </v-dialog>

          <!-- note -->
          <v-list-item
            v-if="
              ['NOTE_CREATED', 'NOTE_DELETED'].includes(item.event) && item.note
            "
            dense
          >
            <v-list-item-action>
              <note-severity-icon :severity="item.note.severity" show-tooltip />
            </v-list-item-action>
            <v-list-item-content>
              <v-list-item-title v-if="item.note.title">
                {{ item.note.title }}
              </v-list-item-title>
              <v-list-item-subtitle>
                <long-text :text="item.note.description" />
              </v-list-item-subtitle>
            </v-list-item-content>
          </v-list-item>
          <v-list-item v-else-if="item.event === 'NOTE_CREATED'">
            <v-list-item-action>
              <v-icon small>$freshvuehistory-event-NOTE_DELETED</v-icon>
            </v-list-item-action>
            <v-list-item-content>
              <v-list-item-title>
                {{ $t('fresh-vue-history.misc.no-note') }}
              </v-list-item-title>
            </v-list-item-content>
          </v-list-item>

          <!-- details slot -->
          <slot name="details-append" :item="item" view="TABLE" />
        </td>
      </tr>
    </v-slide-x-transition>
    <tr v-else>
      <td colspan="6" class="text-center caption grey--text">
        {{ $t('fresh-vue-history.misc.no-history') }}
      </td>
    </tr>
  </v-simple-table>
</template>

<script>
import { CardBase } from '@freshsolutions/fresh-vue-cards'
import { JsonDetails } from '@freshsolutions/fresh-vue'
import { AuditChanges } from '@freshsolutions/fresh-vue-audits'
import { LongText } from '@freshsolutions/fresh-vue'
import { NoteSeverityIcon } from '@freshsolutions/fresh-vue-notes'
import HistoryViewDuration from './HistoryViewDuration'

export default {
  name: 'HistoryViewTable',

  components: {
    CardBase,
    JsonDetails,
    AuditChanges,
    LongText,
    NoteSeverityIcon,
    HistoryViewDuration
  },

  props: {
    history: {
      type: Array,
      required: false,
      default: null
    },

    showDuration: {
      type: Boolean,
      required: false,
      default: false
    }
  },

  computed: {
    empty() {
      return this.history?.length <= 0
    }
  },

  methods: {
    getFormattedEventText(item) {
      if (this.$freshVueHistory.eventTextFormatter) {
        return this.$freshVueHistory.eventTextFormatter(item) ?? item.event
      }
      return item.event
    }
  }
}
</script>
